import moment from 'moment'

import { DEFAULT_FLEXIBLE_DURATION_RANGE } from 'constants/search'

/**
 * Leaf helpers for flexible-date search params.
 * Kept out of searchUtils so typeaheadStreamingMap can map NL date chunks
 * without the typeaheadStreamingMap ↔ searchUtils cycle.
 */

/**
 * Validates if a month is within the acceptable booking range (current month to 3 years from now)
 */
function isValidMonth(date: moment.Moment, maxYears: number = 3): boolean {
  const now = moment().startOf('month')
  const maxDate = moment().add(maxYears, 'years').endOf('month')
  return date.isSameOrAfter(now) && date.isSameOrBefore(maxDate)
}

/**
 * Maps an array of flexible nights to the appropriate duration range
 * @param nights Array of numbers representing flexible nights
 * @returns The appropriate duration range or ANY_DURATION if invalid/empty
 */
export function mapFlexibleNightsToDurationRange(nights: Array<number> | undefined): DEFAULT_FLEXIBLE_DURATION_RANGE {
  if (!nights?.length) return DEFAULT_FLEXIBLE_DURATION_RANGE.ANY_DURATION

  const min = Math.min(...nights)
  const max = Math.max(...nights)

  if (min >= 1 && max <= 3) {
    return DEFAULT_FLEXIBLE_DURATION_RANGE.SHORT_STAY
  } else if (min >= 4 && max <= 7) {
    return DEFAULT_FLEXIBLE_DURATION_RANGE.MEDIUM_STAY
  } else if (min >= 8 && max <= 30) {
    return DEFAULT_FLEXIBLE_DURATION_RANGE.LONG_STAY
  }

  return DEFAULT_FLEXIBLE_DURATION_RANGE.ANY_DURATION
}

/**
 * Converts a date range into a comma-separated string of months in YYYY-MM format
 * @param ranges Array of date ranges with start and end dates
 * @returns Comma-separated string of months or undefined if no ranges provided
 */
export function convertDateRangesToMonthString(ranges: Array<{ start: string, end: string }> | undefined): string | undefined {
  if (!ranges) return undefined

  return ranges
    .flatMap(range => {
      const months: Array<string> = []
      const current = moment(range.start).startOf('month')
      const end = moment(range.end).startOf('month')

      while (current.isSameOrBefore(end)) {
        if (isValidMonth(current)) {
          months.push(current.format('YYYY-MM'))
        }
        current.add(1, 'month')
      }
      return months
    })
    .filter(month => month) // Remove any undefined entries
    .join(',') || undefined // Return undefined if no valid months found
}
